import type { UserProfile } from "../profile/schemas";

/**
 * What an event organiser sends to put themselves on their own event's
 * attendee list: the existing ADD_EVENT_ATTENDEE payload, nothing new.
 *
 * Only identity travels. Job title, company, bio, photo and LinkedIn are left
 * out on purpose: the address belongs to an account, so the backend links the
 * new row to it and fills those blanks from the person's own profile
 * (OnAttendeeLinkedHandler), where the person, not this card, owns them.
 */
export interface SelfAttendeePayload extends Record<string, unknown> {
  name: string;
  email: string;
  attendeeType: "internal";
  status: "confirmed";
}

/**
 * The payload, or null when there is no address to add. Without one the
 * command would create an unlinkable directory listing, which is not "me".
 *
 * The profile's address comes first because the backend finds the account by
 * it (users.email); the sign-in address is the fallback for a profile that has
 * not been read or carries none. The name falls back to the part of the
 * address before the "@", since the command refuses an empty name.
 */
export function selfAttendeePayload(
  profile: Pick<UserProfile, "name" | "email"> | null,
  authEmail: string | null,
): SelfAttendeePayload | null {
  const email = (
    profile?.email.trim() ||
    authEmail?.trim() ||
    ""
  ).toLowerCase();
  if (!email) return null;
  const name = profile?.name.trim() || email.split("@")[0] || email;
  return {
    name: name.slice(0, 200),
    email,
    attendeeType: "internal",
    status: "confirmed",
  };
}

/** The backend's refusal for a row that already sits at this address. */
export const ALREADY_ATTENDEE = /already an attendee/i;

/**
 * Plain wording for a failed add. The trigger's own sentences are written for
 * an organiser typing somebody else's address ("x@y.z is already an attendee
 * of this event"), which reads oddly when the person is adding themselves.
 */
export function addMeFailureMessage(error: string): string {
  if (ALREADY_ATTENDEE.test(error)) {
    return "An attendee record with your email address already exists, but it is not linked to your account. Ask another organiser to check the Attendees tab in Admin.";
  }
  // A rules refusal names nothing. The create is also refused while the event
  // is being deleted, which is the case worth saying.
  if (/permission|insufficient/i.test(error)) {
    return "You cannot be added right now. The event may be closing or being deleted, or your organiser access may have changed.";
  }
  return error;
}
